import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { formatBytes } from "@suite/core";
import { canPersistHandles, matchWarning, type MatchQuality } from "./sources.ts";
import { assign, pickMedia, reopen, supply, type HandleOutcome, type Supplied } from "./relink.ts";
import type { ProjectSummary } from "./store.ts";
import { parseProject, type Project, type SourceRef } from "./timeline.ts";

/** A project chosen but not yet opened: what it needs and what it has so far. */
export interface Staged {
  project: Project;
  handles: Map<string, FileSystemFileHandle>;
}

interface FilesPage extends Staged {
  page: "files";
  /** Whether Back leads anywhere. False when this page was reached from a project file. */
  fromList: boolean;
  supplied: Map<string, Supplied>;
  outcomes: Map<string, HandleOutcome>;
  unmatched: string[];
  busy: boolean;
  triedHandles: boolean;
}
type Page = { page: "list" } | FilesPage;

/**
 * The dialog that opens the studio when there is something to reopen.
 *
 * Page one lists the projects in this browser. Page two is the chosen
 * project's files: the browser kept the edit and a description of each file,
 * not the file, so each has to be pointed at again and is checked against
 * that description. Missing ones are fine; the project opens with them blank
 * and each track keeps a way to supply its file later.
 */
export function OpenDialog({
  projects,
  start,
  load,
  onNew,
  onOpen,
  onDelete,
  onClose,
}: {
  projects: ProjectSummary[];
  /** Skip the list and go straight to this project's files. */
  start?: Staged;
  load: (id: string) => Promise<Staged | null>;
  onNew: () => void;
  onOpen: (project: Project, supplied: Map<string, Supplied>) => void;
  onDelete: (id: string) => void;
  /** Present when there is an editor behind the dialog to go back to. */
  onClose?: () => void;
}) {
  const titleId = useId();
  const [state, setState] = useState<Page>(() => (start ? filesPage(start, false) : { page: "list" }));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!onClose) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const choose = async (id: string) => {
    setError(null);
    try {
      const staged = await load(id);
      if (!staged) throw new Error("That project is no longer in this browser.");
      setState(filesPage(staged, true));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not open that project.");
    }
  };

  const openFile = async () => {
    const [picked] = await pickMedia({ multiple: false, accept: ".json,application/json" });
    if (!picked) return;
    setError(null);
    try {
      const project = parseProject(await picked.file.text());
      setState(filesPage({ project, handles: new Map() }, true));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That is not a project file we can read.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex max-h-[min(88dvh,720px)] w-full max-w-[560px] flex-col rounded-[14px] border border-rule bg-surface shadow-[0_18px_48px_rgb(51_41_31/0.28)]"
      >
        {state.page === "list" ? (
          <ProjectList
            titleId={titleId}
            projects={projects}
            error={error}
            onChoose={(id) => void choose(id)}
            onDelete={onDelete}
            onOpenFile={() => void openFile()}
            onNew={onNew}
            onClose={onClose}
          />
        ) : (
          <FilesPageView
            titleId={titleId}
            state={state}
            setState={(next) => setState(next)}
            onBack={state.fromList ? () => setState({ page: "list" }) : undefined}
            onOpen={() => onOpen(state.project, state.supplied)}
          />
        )}
      </div>
    </div>
  );
}

const filesPage = (staged: Staged, fromList: boolean): FilesPage => ({
  page: "files",
  ...staged,
  fromList,
  supplied: new Map(),
  outcomes: new Map(),
  unmatched: [],
  busy: false,
  triedHandles: false,
});

/* ---- page one: the list -------------------------------------------------- */

function ProjectList({
  titleId,
  projects,
  error,
  onChoose,
  onDelete,
  onOpenFile,
  onNew,
  onClose,
}: {
  titleId: string;
  projects: ProjectSummary[];
  error: string | null;
  onChoose: (id: string) => void;
  onDelete: (id: string) => void;
  onOpenFile: () => void;
  onNew: () => void;
  onClose?: () => void;
}) {
  return (
    <>
      <div className="px-5 pt-5">
        <h2 id={titleId} className="font-serif text-[22px] font-semibold leading-tight text-pine">
          Your projects
        </h2>
        <OtterSays picture="at-laptop">
          {projects.length === 0
            ? "Nothing saved in this browser yet. Start one and I'll remember it as you go."
            : "I remember these from this browser: what went where, and which files. Never the files themselves, so pick one and we'll find those together."}
        </OtterSays>
      </div>

      <div className="scroll-thin mt-4 flex-1 overflow-y-auto px-5">
        {projects.length === 0 ? (
          <p className="rounded-[10px] border border-dashed border-rule px-4 py-6 text-center font-serif text-[15px] italic text-muted">
            Nothing saved here yet.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {projects.map((summary) => (
              <li key={summary.id} className="flex items-center gap-3 rounded-[10px] border border-rule bg-cream/40 p-2">
                <button
                  type="button"
                  onClick={() => onChoose(summary.id)}
                  className="flex min-w-0 flex-1 items-center gap-3 rounded-[8px] text-left transition-colors hover:bg-cream-2/60"
                >
                  <Poster poster={summary.poster} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] font-medium text-ink">{summary.name}</span>
                    <span className="block font-mono text-[10.5px] text-muted">
                      {ago(summary.updatedAt)} · {clock(summary.duration)} · {summary.sourceCount}{" "}
                      {summary.sourceCount === 1 ? "file" : "files"}
                    </span>
                  </span>
                </button>
                <button type="button" onClick={() => onChoose(summary.id)} className="pill shrink-0">
                  Open
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(summary.id)}
                  aria-label={`Forget ${summary.name}`}
                  title="Forget this project. Your files are untouched."
                  className="flex size-8 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:bg-danger/10 hover:text-danger"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        )}
        {error ? <p className="mt-3 text-[12px] text-danger">{error}</p> : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-4">
        <button type="button" onClick={onOpenFile} className="link text-[12.5px] text-soft hover:text-pine">
          Open a project file…
        </button>
        <div className="flex gap-2">
          {onClose ? (
            <button type="button" onClick={onClose} className="pill">
              Cancel
            </button>
          ) : null}
          <button type="button" onClick={onNew} className="pill-solid">
            Start new
          </button>
        </div>
      </div>
    </>
  );
}

function Poster({ poster }: { poster?: string }) {
  return poster ? (
    <img src={poster} alt="" className="h-10 w-16 shrink-0 rounded-[6px] object-cover" />
  ) : (
    <span className="flex h-10 w-16 shrink-0 items-center justify-center rounded-[6px] border border-rule bg-cream-2/50 text-pine">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 18V5l12-2v13" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="18" cy="16" r="3" />
      </svg>
    </span>
  );
}

/* ---- page two: the files ------------------------------------------------- */

function FilesPageView({
  titleId,
  state,
  setState,
  onBack,
  onOpen,
}: {
  titleId: string;
  state: FilesPage;
  setState: (next: FilesPage) => void;
  onBack?: () => void;
  onOpen: () => void;
}) {
  const live = useRef(state);
  live.current = state;
  const update = (patch: Partial<FilesPage>) => {
    live.current = { ...live.current, ...patch };
    setState(live.current);
  };

  const real = state.project.sources.filter((source) => !source.derived);
  const derived = state.project.sources.filter((source) => source.derived);
  const missing = real.filter((source) => !state.supplied.has(source.id));
  const withHandles = missing.filter((source) => state.handles.has(source.id));
  const canReopen = canPersistHandles() && withHandles.length > 0 && !state.triedHandles;

  const merge = (more: Map<string, Supplied>) => {
    const supplied = new Map(live.current.supplied);
    for (const [id, entry] of more) supplied.set(id, entry);
    update({ supplied });
  };

  const reopenAll = async () => {
    update({ busy: true, triedHandles: true });
    const outcomes = new Map(live.current.outcomes);
    const got = await reopen(missing, state.handles, (ref, outcome) => {
      outcomes.set(ref.id, outcome);
      update({ outcomes: new Map(outcomes) });
    });
    merge(got);
    update({ busy: false });
  };

  const chooseMany = async () => {
    const picked = await pickMedia({ multiple: true });
    if (picked.length === 0) return;
    update({ busy: true });
    const { matched, unmatched } = await assign(missing, picked);
    merge(matched);
    update({ busy: false, unmatched: unmatched.map((file) => file.name) });
  };

  const chooseOne = async (ref: SourceRef) => {
    const [picked] = await pickMedia({ multiple: false });
    if (!picked) return;
    update({ busy: true });
    merge(new Map([[ref.id, await supply(ref, picked)]]));
    update({ busy: false, unmatched: [] });
  };

  const have = state.supplied.size;

  return (
    <>
      <div className="px-5 pt-5">
        <h2 id={titleId} className="font-serif text-[22px] font-semibold leading-tight text-pine">
          {state.project.name}
        </h2>
        <OtterSays picture="passing-files">{otterLine(real.length, have, [...state.supplied.values()].some((entry) => entry.quality !== "exact"))}</OtterSays>
      </div>

      {real.length > 0 ? (
        <div className="mt-4 flex flex-wrap items-center gap-2 px-5">
          {canReopen ? (
            <button type="button" onClick={() => void reopenAll()} disabled={state.busy} className="pill-solid">
              <FolderIcon />
              Open the files
              <span className="font-mono text-[10.5px] font-normal opacity-80">
                · asks permission
              </span>
            </button>
          ) : null}
          {missing.length > 0 ? (
            <button type="button" onClick={() => void chooseMany()} disabled={state.busy} className="pill">
              Choose files…
            </button>
          ) : null}
          <span className={`ml-auto font-mono text-[11px] ${missing.length === 0 ? "text-ok" : "text-muted"}`}>
            {missing.length === 0 ? "all ready" : `${have} of ${real.length} ready`}
          </span>
        </div>
      ) : null}

      <div className="scroll-thin mt-3 flex-1 overflow-y-auto px-5">
        <ul className="flex flex-col gap-1.5">
          {real.map((source) => (
            <FileRow
              key={source.id}
              source={source}
              supplied={state.supplied.get(source.id)}
              outcome={state.outcomes.get(source.id)}
              busy={state.busy}
              onChoose={() => void chooseOne(source)}
            />
          ))}
          {derived.map((source) => {
            const origin = state.project.sources.find((entry) => entry.id === source.derived?.from);
            return (
              <li key={source.id} className="flex items-center gap-3 rounded-[8px] border border-dashed border-rule px-3 py-2 text-[12px] text-muted">
                <span className="truncate">{source.name}</span>
                <span className="ml-auto shrink-0 font-mono text-[10.5px]">
                  rebuilt from {origin?.name ?? "its original"} after opening
                </span>
              </li>
            );
          })}
        </ul>
        {state.unmatched.length > 0 ? (
          <p className="mt-3 text-[12px] leading-[1.5] text-soft">
            Not used, since {state.unmatched.length === 1 ? "it doesn't" : "they don't"} match any file this
            project is waiting for: {state.unmatched.join(", ")}. To use one anyway, choose it on the row it belongs to.
          </p>
        ) : null}
      </div>

      <div className="flex items-center justify-between gap-2 px-5 py-4">
        {onBack ? (
          <button type="button" onClick={onBack} className="pill">
            Back
          </button>
        ) : (
          <span />
        )}
        <button type="button" onClick={onOpen} disabled={state.busy} className="pill-solid">
          {have === 0 ? "Open as is" : have === real.length ? "Open" : `Open with ${have} of ${real.length} files`}
        </button>
      </div>
    </>
  );
}

function FileRow({
  source,
  supplied,
  outcome,
  busy,
  onChoose,
}: {
  source: SourceRef;
  supplied?: Supplied;
  outcome?: HandleOutcome;
  busy: boolean;
  onChoose: () => void;
}) {
  const quality: MatchQuality | null = supplied?.quality ?? null;
  const tone =
    quality === "exact" ? "text-ok" : quality ? "text-warn" : outcome === "gone" || outcome === "denied" ? "text-warn" : "text-muted";
  const status = supplied
    ? quality === "exact"
      ? "Matched"
      : quality === "changed"
        ? "Different version"
        : "Doesn't look like it"
    : outcome === "gone"
      ? "Moved or renamed"
      : outcome === "denied"
        ? "Permission not given"
        : "Waiting";
  return (
    <li className="flex items-center gap-3 rounded-[8px] border border-rule bg-cream/40 px-3 py-2">
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] text-ink" title={source.name}>
          {source.name}
        </span>
        <span className="block font-mono text-[10.5px] text-muted">
          {formatBytes(source.size)} · {clock(source.duration)}
          {supplied && quality !== "exact" ? ` · ${matchWarning(quality!, source)}` : ""}
        </span>
      </span>
      <span className={`shrink-0 font-mono text-[10.5px] uppercase tracking-[0.08em] ${tone}`}>{status}</span>
      <button type="button" onClick={onChoose} disabled={busy} className="pill shrink-0">
        {supplied ? "Change" : "Choose"}
      </button>
    </li>
  );
}

/** What the otter says on the files page, depending on how it is going. */
function otterLine(total: number, have: number, anyDoubtful: boolean): string {
  if (total === 0) return "This one has no files to find. Straight in!";
  if (have === 0) {
    return "I kept your edit, not your files. Point me at each one and I'll check it's the same file you used. Skip any you like and I'll leave a blank clip for later.";
  }
  if (have < total) return `${have} of ${total} found. Keep going, or open it now and fill the rest in later.`;
  return anyDoubtful
    ? "That's all of them! One or two look a bit different from before, so give those a listen."
    : "That's all of them, and they match what you used before. Off we go!";
}

/**
 * An otter with a speech bubble, for the bits of text that talk to you.
 * The picture is chosen for what the page is about: two otters passing a
 * folder across for the files page, one at a laptop for the project list and
 * for the export, which is the otter working away while you wait.
 */
export function OtterSays({
  picture,
  children,
  className = "mt-3",
}: {
  picture: "passing-files" | "at-laptop";
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex items-end gap-3 ${className}`}>
      <img
        src={picture === "passing-files" ? "/otter/otters-passing-files.png" : "/otter/otter-at-laptop.png"}
        alt=""
        aria-hidden="true"
        className="h-[84px] w-auto shrink-0"
      />
      <p className="bubble relative rounded-[12px] border border-rule bg-cream px-3.5 py-2.5 text-[13px] leading-[1.5] text-ink">
        {children}
      </p>
    </div>
  );
}

const FolderIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);

function clock(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

function ago(when: number): string {
  const seconds = Math.max(0, (Date.now() - when) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h ago`;
  if (seconds < 86400 * 7) return `${Math.round(seconds / 86400)} d ago`;
  return new Date(when).toLocaleDateString();
}
